import { Alert } from "antd";
import type { SchematizedValues } from "components/Form/components/SchemaForm/SchemaForm.types";
import { schemaFormValidator } from "components/Form/Form.utils";
import { IconCollection } from "components/Icon/IconCollection";
import RecordLabel from "components/RecordLabel";
import { TimeAgoWithDateTimeFormatter } from "components/TimeAgo/TimeAgoWithDateTimeFormatter";
import { ResourceConfig } from "config/config";
import {
    deleteActionSignature,
    DeleteListAction,
    deletePermanentlyActionSignature,
    duplicateActionSignature,
    exportActionSignature,
    moveToLocationActionSignature,
    PermanentlyDeleteListAction,
} from "containers/Resources/components/CommonActions/CommonActions";
import {
    commentsTab,
    logsTab,
    logsTabKey,
    metadataTab,
    referencesUsedByTab,
    valueAndSchema,
    wrappersTab,
} from "containers/Resources/screens/ResourcesSingle/constants";
import { isEnabledColumn, nextRunsColumn } from "containers/Search/components/SearchTabs/columns";
import { Schedule } from "graphql/generated/graphql";
import { isEqual } from "lodash";
import { getRelativeUrl } from "utils/service";
import { serializeToPrettyYaml } from "utils/valueTransformers";
import identity from "./identity";
import resource from "./resource";
import { enabledScheduleField, getLatestExecutions } from "./sharedFields";

// Gate config-editing on the PERSISTED (backend-returned) is_enabled, NOT the
// current unsaved toggle value. The backend forbids changing a schedule's
// configuration while the persisted record is enabled. If the lock keyed off the
// live toggle, a user could toggle the switch to disabled (no save), edit the
// config (no save), toggle back to enabled and save — the diff then drops the
// unchanged is_enabled and sends only the config change, which the backend
// correctly rejects ("Cannot change a schedule while it is enabled"). Keying off
// the persisted value keeps config locked until the disable is actually SAVED,
// matching backend semantics (disable + save, then edit + save, then re-enable).
// (operator-task-fe-schedule-edit-enabled-toggle-stale-flag-rejects-save)
export const isSchedulePersistedEnabled = (_currentValues, persistedValues) => {
    return persistedValues?.is_enabled === true;
};

const enabledScheduleProps = {
    getDisabled: isSchedulePersistedEnabled,
    fieldTooltip: (currentValues, persistedValues) => {
        if (isSchedulePersistedEnabled(currentValues, persistedValues)) {
            return "Disable the schedule and save before changing this field.";
        }
    },
};

const isEnabledFieldDisabled = (currentValues) => {
    return !(currentValues?.flow_id && currentValues?.scheduler_id);
};

const isEnabledFieldProps = {
    getDisabled: isEnabledFieldDisabled,
    fieldTooltip: (currentValues = {} as Schedule) => {
        if (!isEnabledFieldDisabled(currentValues)) {
            return;
        }
        const { flow_id, scheduler_id } = currentValues;

        const getText = () => {
            const missingItems: string[] = [];
            if (!scheduler_id) missingItems.push("a scheduler");
            if (!flow_id) missingItems.push("a flow");

            return missingItems.join(", ");
        };

        return `To enable a schedule, select ${getText()} first.`;
    },
};

const resourceName = "schedule";

// NOTE: there is deliberately NO "Stamp input schema" button on the schedule
// screen. A scheduled flow's inputs are defined by its scheduler, not by a
// platform-standard envelope, so the backend schedule stamp endpoint was
// dropped (cloudomation resources/schedule.py; commit 7f83a5592 "drop schedule
// stamp_input_schema endpoint (wrong surface)"). Schedule-input-schema stamping
// is delivered instead by the Scheduling bundle's schedule-scoped "Stamp input
// schema on flow" plugin action. The webhook stamp button (webhook.tsx) is
// unaffected — webhooks DO have a fixed platform envelope + endpoint.

const config: ResourceConfig = {
    label: "Schedule",
    icon: IconCollection.timetable,
    resourceName,
    resourceClass: "resource",
    defaultActiveTab: logsTabKey,
    layout: [
        { partial: "pageHeaderForm" },
        { partial: "form" },
        {
            partial: "tabs",
            config: {
                tabs: [logsTab.tab, wrappersTab.tab, commentsTab.tab, referencesUsedByTab.tab, metadataTab.tab],
                content: [
                    logsTab.content,
                    wrappersTab.content,
                    commentsTab.content,
                    referencesUsedByTab.content,
                    metadataTab.content,
                ],
            },
        },
    ],
    actions: {
        shown: [],
        hidden: [
            duplicateActionSignature,
            exportActionSignature,
            moveToLocationActionSignature,
            deleteActionSignature,
            deletePermanentlyActionSignature,
        ],
    },
    listActions: {
        shown: [],
        hidden: [
            { key: "delete", action: DeleteListAction },
            { key: "deletePermanently", action: PermanentlyDeleteListAction },
        ],
    },
    additionalColumns: (setNewValue) => [
        isEnabledColumn(setNewValue),
        {
            label: "Scheduler",
            key: "scheduler",
            shown: true,
            dto: (value: any) =>
                value?.scheduler_id_scheduler?.id && (
                    <RecordLabel
                        link={getRelativeUrl("scheduler", value.scheduler_id_scheduler.id)}
                        name={value.scheduler_id_scheduler.name}
                        iconByType="scheduler"
                    />
                ),
        },
        {
            label: "Flow",
            key: "flow",
            shown: true,
            dto: (value: any) =>
                value?.flow_id_flow?.id && (
                    <RecordLabel
                        link={getRelativeUrl("flow", value.flow_id_flow.id)}
                        name={value?.flow_id_flow.name}
                        iconByType="flow"
                    />
                ),
        },
        nextRunsColumn(),
    ],
    graphql: {
        resource: resourceName,
        resourceBody: `
            ${resource.graphql.resourceBody}
            ${identity.graphql.resourceBody}
            scheduler_id
            scheduler_id_scheduler {
                id
                name
                configuration_schema
            }
            flow_id
            flow_id_flow {
                id
                name
            }
            is_enabled
            last_activity
            next_runs
            configuration
        `,
        resourceBodyListing: `
            ${resource.graphql.resourceBody}
            is_enabled
            scheduler_id_scheduler {
                id
                name
            }
            flow_id_flow {
                id
                name
            }
            next_runs
        `,
        updateVariables: `
            ${resource.graphql.updateVariables(resourceName)}
            $scheduler_id: String,
            $flow_id: String,
            $is_enabled: Boolean,
            $configuration: SchemalessData,
        `,
        updateQuery: `
            ${resource.graphql.updateQuery(resourceName)}
            scheduler_id: $scheduler_id,
            flow_id: $flow_id,
            is_enabled: $is_enabled,
            configuration: $configuration,
        `,
        createVariables: `
            ${resource.graphql.createVariables(resourceName)}
            $scheduler_id: String,
            $flow_id: String,
            $is_enabled: Boolean,
            $configuration: SchemalessData,
        `,
        createQuery: `
            ${resource.graphql.createQuery(resourceName)}
            scheduler_id: $scheduler_id,
            flow_id: $flow_id,
            is_enabled: $is_enabled,
            configuration: $configuration
        `,
    },
    statsSidebar: [getLatestExecutions("schedule")],
    form: [
        {
            name: "scheduler_id",
            component: "autocomplete",
            fieldProps: {
                label: "Scheduler",
                options: {
                    resource: "scheduler",
                },
                docs: {
                    text: "Choose the type of the schedule",
                    url: "/docs/scheduling",
                },
            },
            fieldAttr: {
                ...enabledScheduleProps,
            },
        },
        {
            name: "flow_id",
            component: "autocomplete",
            fieldProps: {
                label: "Flow",
                options: {
                    resource: "flow",
                },
                docs: {
                    text: "Choose a Flow to run on schedule",
                    url: "/docs/scheduling",
                },
            },
            fieldAttr: {
                ...enabledScheduleProps,
            },
        },
        enabledScheduleField(isEnabledFieldProps),

        {
            name: valueAndSchema,
            component: "schemaFormConditional",
            fieldProps: {
                lang: "yaml",
                isReadOnly: false,
                canEnableSaveManually: true,
                fieldReplacement: (
                    <Alert message="Please choose a scheduler." type="info" showIcon />
                ),
                isConditionMet: (currentValues) => currentValues.scheduler_id != null,
            },
            remappingSubmitDto: (value: SchematizedValues) => {
                return [
                    {
                        key: "configuration",
                        value: value.values,
                    },
                ];
            },
            isEqual: (valueA, valueB) => {
                return isEqual(valueA, valueB);
            },
            validation: schemaFormValidator,
            fieldAttr: { getDisabled: isSchedulePersistedEnabled },
        },
        {
            name: "next_runs",
            component: "codeEditor",
            fieldProps: {
                label: "Next runs",
                lang: "yaml",
                isReadOnly: true,
                valueTransformer: serializeToPrettyYaml,
                // Read-only JSON⇄YAML display toggle for this structured value.
                structuredDataView: true,
            },
        },
    ],
    detailsSidebar: [
        {
            label: "Last activity",
            valueKey: "last_activity",
            dto: (value: any) => (value ? <TimeAgoWithDateTimeFormatter datetime={value} /> : null),
        },
    ],
    formDTO: {
        initialValueDTO: (resourceData: any) => {
            const inputSchema = resourceData?.scheduler_id_scheduler?.configuration_schema ?? {};
            const valueAndSchemaFieldValues: SchematizedValues = {
                values: resourceData?.configuration,
                schema: inputSchema,
            };
            return { ...resourceData, [valueAndSchema]: valueAndSchemaFieldValues };
        },
    },
};
export default config;
